import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, message, Spin, List, Tag, Typography, theme } from 'antd';
import { SettingOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { getPaymentTypes, updatePaymentType, updateMultiplePaymentTypes } from '../../../services/settings/paymentApi';

const { Text } = Typography;

const PaymentSettings = () => {
  const { token } = theme.useToken();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const fetchPaymentTypes = async () => {
    setLoading(true);
    try {
      const response = await getPaymentTypes();
      setPaymentMethods(response.data.payment_methods);
    } catch (error) {
      console.error('Error fetching payment types:', error);
      message.error('Failed to fetch payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (paymentType, isActive) => {
    const originalState = paymentMethods.find(m => m.type === paymentType)?.is_active;
    
    // Optimistic update
    setPaymentMethods(prev => 
      prev.map(method => 
        method.type === paymentType 
          ? { ...method, is_active: isActive }
          : method
      )
    );
    setHasChanges(true);

    try {
      await updatePaymentType({ 
        payment_type: paymentType, 
        is_active: isActive 
      });
      
      message.success(`${getPaymentMethodName(paymentType)} ${isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating payment type:', error);
      
      // Revert on error
      setPaymentMethods(prev => 
        prev.map(method => 
          method.type === paymentType 
            ? { ...method, is_active: originalState }
            : method
        )
      );
      
      message.error('Failed to update payment method');
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await updateMultiplePaymentTypes({
        payment_methods: paymentMethods
      });
      message.success('All payment settings updated successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error updating multiple payment types:', error);
      message.error('Failed to update payment settings');
    } finally {
      setSaving(false);
    }
  };

  const getPaymentMethodName = (type) => {
    const names = {
      stripe: 'Stripe',
      paypal: 'PayPal',
      cryptomus: 'Cryptomus',
      whatsapp: 'WhatsApp',
      pop: 'POP Payment'
    };
    return names[type] || type;
  };

  const getPaymentMethodDescription = (type) => {
    const descriptions = {
      stripe: 'Credit card payments via Stripe',
      paypal: 'Pay with PayPal account',
      cryptomus: 'Cryptocurrency payments',
      whatsapp: 'Payment via WhatsApp message',
      pop: 'Point of Purchase payment'
    };
    return descriptions[type] || 'Payment method';
  };

  const getPaymentMethodIcon = (type) => {
    const icons = {
      stripe: '💳',
      paypal: '🔵',
      cryptomus: '₿',
      whatsapp: '💬',
      pop: '🏪'
    };
    return icons[type] || '💰';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SettingOutlined />
          <span>Payment Settings</span>
        </div>
      }
      style={{ margin: '16px' }}
      // extra={
      //   <Button 
      //     type="primary" 
      //     onClick={handleSaveAll}
      //     loading={saving}
      //     disabled={!hasChanges}
      //   >
      //     Save All Changes
      //   </Button>
      // }
    >
      <List
        dataSource={paymentMethods}
        renderItem={(method) => (
          <List.Item
            actions={[
              <Switch
                key={method.type}
                checked={method.is_active}
                onChange={(checked) => handleToggle(method.type, checked)}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            ]}
          >
            <List.Item.Meta
              avatar={
                <span style={{ fontSize: '24px' }}>
                  {getPaymentMethodIcon(method.type)}
                </span>
              }
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text strong>{getPaymentMethodName(method.type)}</Text>
                  {method.is_active ? (
                    <Tag color="green">Active</Tag>
                  ) : (
                    <Tag color="default">Inactive</Tag>
                  )}
                </div>
              }
              description={getPaymentMethodDescription(method.type)}
            />
          </List.Item>
        )}
      />
      
      <div style={{
        marginTop: 16,
        padding: 16,
        backgroundColor: token.colorFillAlter,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
      }}>
        <Text type="secondary">
          <strong>Note:</strong> Multiple payment methods can be active simultaneously. 
          Users will see all active payment options during checkout. Changes are saved automatically when you toggle switches.
        </Text>
      </div>

      <div style={{ marginTop: '16px' }}>
        <Text strong>Active Payment Methods: </Text>
        {paymentMethods?.filter(m => m.is_active).length > 0 ? (
          paymentMethods
            .filter(m => m.is_active)
            .map(method => (
              <Tag key={method.type} color="blue" style={{ margin: '4px' }}>
                {getPaymentMethodName(method.type)}
              </Tag>
            ))
        ) : (
          <Text type="secondary" italic>No active payment methods</Text>
        )}
      </div>
    </Card>
  );
};

export default PaymentSettings;
